import {
  Box,
  Flex,
  IconButton,
  Spinner,
} from '@ouster/webviz-react-components';

import { format } from 'date-fns/format';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AreaChart,
  Area,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

// Define specific props for your card
interface ChartCardProps {
  title: string;
  data: any[];
  ticks?: number[]; // Optional: For custom X-axis ticks
  dataKey: string;
  displayName: string;
  isLoading: boolean;
  downloadHandler: () => void;
  tickFormatter?: (value: number) => string;
  chartType?: 'area' | 'bar';
}

interface ChartDataPoint {
  fullDate: string;
  [key: string]: string | number;
}

type CustomTooltipProps = TooltipProps<ValueType, NameType> & {
  payload?: Array<{
    payload: ChartDataPoint;
    name: string;
    value: number;
    color: string;
  }>;
};

// Define the exact props for the CustomTooltip
// We use ValueType and NameType to match Recharts internal data expectations
const CustomTooltip = ({
  active,
  payload,
  displayName,
}: CustomTooltipProps & { displayName: string }) => {
  // 1. Check if the tooltip is active and payload exists
  if (active && payload && payload.length) {
    // 2. Extract the specific data point from the payload
    const data = payload[0].payload;

    return (
      <Box
        bg="gray.900"
        border="1px solid"
        borderColor="gray.600"
        p={3}
        borderRadius="md"
        style={{ pointerEvents: 'none' }}
      >
        {/* Accessing data through the 'payload' array item */}
        <Box color="gray.400" fontSize="xs" mb={1}>
          {data.fullDate}
        </Box>
        <Box color="white" fontWeight="bold" fontSize="sm">
          {displayName}: {payload[0].value}
        </Box>
      </Box>
    );
  }

  return null;
};

// Main Card Component
export default function ChartCard({
  title,
  data,
  dataKey,
  displayName,
  isLoading,
  downloadHandler,
  ticks,
  tickFormatter = (unixTime: number) => format(new Date(unixTime), 'MMM yyyy'),
  chartType = 'area',
}: ChartCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const hasChartValue = useMemo(
    () =>
      data.some((point: Record<string, unknown>) => {
        const rawValue = point[dataKey];

        if (rawValue === null || rawValue === undefined) {
          return false;
        }

        const numericValue = Number(rawValue);
        return Number.isFinite(numericValue) && numericValue > 0;
      }),
    [data, dataKey]
  );

  const isNoValueState = !isLoading && !hasChartValue;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === cardRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const areaXAxisDomain = useMemo(() => {
    if (!ticks || ticks.length === 0) {
      return ['auto', 'auto'] as const;
    }

    if (ticks.length === 1) {
      return [ticks[0] - 86400000, ticks[0] + 86400000] as const;
    }

    // Inset first/last ticks so number-axis spacing visually matches bar-chart category padding.
    const leftPad = (ticks[1] - ticks[0]) / 2;
    const rightPad = (ticks[ticks.length - 1] - ticks[ticks.length - 2]) / 2;

    return [ticks[0] - leftPad, ticks[ticks.length - 1] + rightPad] as const;
  }, [ticks]);

  const toggleFullscreen = async () => {
    if (document.fullscreenElement === cardRef.current) {
      await document.exitFullscreen();
      return;
    }

    if (cardRef.current) {
      await cardRef.current.requestFullscreen();
    }
  };

  return (
    <Box
      ref={cardRef}
      bg="gray.800"
      p={6}
      borderRadius="lg"
      flex="1"
      minW={isFullscreen ? 'auto' : '480'}
      border="1px solid"
      borderColor="gray.700"
      h={isFullscreen ? '100%' : 'auto'}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb={10}
        fontSize="sm"
        fontWeight="bold"
        color="gray.300"
        textTransform="uppercase"
      >
        <span>{title}</span>
        <Flex>
          <IconButton
            icon={isFullscreen ? 'closeFullScreen' : 'fullscreen'}
            emphasis="quiet"
            variant="ghost"
            onClick={toggleFullscreen}
          />
          <IconButton
            icon="download"
            emphasis="quiet"
            variant="ghost"
            onClick={downloadHandler}
          />
        </Flex>
      </Flex>

      {isLoading ? (
        <Box
          h={isFullscreen ? 'calc(100vh - 140px)' : '300px'}
          w="full"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner size="lg" />
        </Box>
      ) : (
        <Box
          h={isFullscreen ? 'calc(100vh - 140px)' : '300px'}
          w="full"
          position="relative"
        >
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={data}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#374151"
                />
                <XAxis
                  dataKey="fullDate"
                  type="category"
                  minTickGap={50}
                  stroke="#9ca3af"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  dy={10}
                  hide={isNoValueState}
                />
                <YAxis
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  hide={isNoValueState}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  content={<CustomTooltip displayName={displayName} />}
                  wrapperStyle={{
                    display: isNoValueState ? 'none' : undefined,
                  }}
                />
                {!isNoValueState && (
                  <Bar
                    dataKey={dataKey}
                    name={dataKey}
                    fill="var(--color-blast-off-blue)"
                    radius={[4, 4, 0, 0]}
                  />
                )}
              </BarChart>
            ) : (
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor="var(--color-blast-off-blue)"
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--color-blast-off-blue)"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#374151"
                />
                <XAxis
                  dataKey="timestamp"
                  type="number" // Crucial: Treats axis as a continuous timeline
                  domain={areaXAxisDomain}
                  scale="time"
                  ticks={ticks}
                  interval="equidistantPreserveStart"
                  tickFormatter={tickFormatter}
                  minTickGap={48}
                  stroke="#9ca3af"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  dy={10}
                  hide={isNoValueState}
                />
                <YAxis
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  hide={isNoValueState}
                />
                {/* Pass the custom component to Tooltip content */}
                <Tooltip
                  content={<CustomTooltip displayName={displayName} />}
                  wrapperStyle={{
                    display: isNoValueState ? 'none' : undefined,
                  }}
                />
                {!isNoValueState && (
                  <Area
                    type="monotone"
                    dataKey={dataKey}
                    name={dataKey}
                    stroke="#3b82f6"
                    fillOpacity={1}
                    fill="url(#colorGrowth)"
                  />
                )}
              </AreaChart>
            )}
          </ResponsiveContainer>
          {isNoValueState && (
            <Box
              position="absolute"
              inset={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.400"
              fontSize="sm"
              fontWeight="medium"
              pointerEvents="none"
            >
              No data available
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
